<script setup>
// Paleta de nós (esquerda) — seções colapsáveis idênticas ao original.
// Arrastar (dnd) ou clicar adiciona o nó. Filtra por tipo de flow e canais WhatsApp.
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { SECTIONS, nodeMeta, nodeColor } from './flowNodeTypes';

const props = defineProps({
  flowType: { type: String, default: 'chatbot' },
  isWhatsapp: { type: Boolean, default: false },
});

const emit = defineEmits(['add']);

const { t } = useI18n();

const expanded = ref(new Set(SECTIONS.map(s => s.id)));
const toggle = id => {
  const n = new Set(expanded.value);
  if (n.has(id)) n.delete(id);
  else n.add(id);
  expanded.value = n;
};

const visibleNodes = section =>
  section.nodes.filter(type => {
    if (props.flowType === 'sequence') {
      if (type === 'delay') return false;
    } else if (type === 'sequenceDelay' || type === 'checkResponse')
      return false;
    if (nodeMeta(type).whatsappOnly && !props.isWhatsapp) return false;
    return true;
  });

const onDragStart = (e, type) => {
  e.dataTransfer.setData('application/vueflow', type);
  e.dataTransfer.effectAllowed = 'move';
};
</script>

<template>
  <div class="p-4 min-h-full bg-n-background">
    <h2 class="text-heading-2 mb-1 text-n-slate-12">
      {{ t('CHATBOT.nodes.sidebar.title') }}
    </h2>
    <p class="mb-4 text-xs text-n-slate-10">
      {{ t('CHATBOT.nodes.sidebar.subtitle') }}
    </p>

    <div class="space-y-3">
      <div
        v-for="section in SECTIONS"
        :key="section.id"
        class="overflow-hidden border rounded-lg border-n-weak"
      >
        <button
          class="flex items-center justify-between w-full px-3 py-2 text-left bg-n-alpha-1 hover:bg-n-alpha-2"
          @click="toggle(section.id)"
        >
          <span class="flex items-center gap-2">
            <svg
              class="w-4 h-4 text-n-slate-11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                v-for="(d, i) in section.icon"
                :key="i"
                stroke-linecap="round"
                stroke-linejoin="round"
                :d="d"
              />
            </svg>
            <span class="text-xs font-semibold text-n-slate-12">{{
              t(section.title)
            }}</span>
          </span>
          <svg
            class="w-4 h-4 transition-transform text-n-slate-10"
            :class="{ 'rotate-180': expanded.has(section.id) }"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        <div v-if="expanded.has(section.id)" class="p-2 space-y-2">
          <div
            v-for="type in visibleNodes(section)"
            :key="type"
            draggable="true"
            class="p-2 border rounded-md cursor-move hover:shadow-md"
            :style="{
              background: nodeColor(type).bg,
              borderColor: nodeColor(type).border,
            }"
            @dragstart="onDragStart($event, type)"
            @click="emit('add', type)"
          >
            <div class="flex items-start gap-2">
              <svg
                class="w-5 h-5 mt-0.5 shrink-0"
                :style="{ color: nodeColor(type).accent }"
                viewBox="0 0 24 24"
                :fill="nodeMeta(type).fill ? 'currentColor' : 'none'"
                :stroke="nodeMeta(type).fill ? 'none' : 'currentColor'"
                stroke-width="2"
              >
                <path
                  v-for="(d, i) in nodeMeta(type).icon"
                  :key="i"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  :d="d"
                />
              </svg>
              <div class="flex-1 min-w-0">
                <p
                  class="text-xs font-medium truncate"
                  :style="{ color: nodeColor(type).accent }"
                >
                  {{ t(nodeMeta(type).label) }}
                </p>
                <p class="text-xs truncate text-n-slate-10">
                  {{ t(nodeMeta(type).desc) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="p-3 mt-4 border rounded-lg bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800"
    >
      <p class="text-xs text-blue-800 dark:text-blue-300">
        <strong>{{ t('CHATBOT.nodes.sidebar.tipLabel') }}</strong>
        {{ t('CHATBOT.nodes.sidebar.tip') }}
      </p>
    </div>
  </div>
</template>
